import { selectAlternatives } from '#cart-recommendations-state';

/**
 * ARIA tabs with on-demand homepage panels. Section Rendering API responses
 * retain complete panels; one request is shared by every tab in an instance.
 * This module executes once per asset URL across multiple section instances.
 *
 * Deferred panels ship a skeleton carousel (snippets/deferred-catalog-status.liquid)
 * and are fetched before they are needed where possible: when a pointer or focus
 * reaches the tab row, or, once the section is near the viewport, in an idle slot.
 * Most tab switches therefore land on ready content; the skeleton only shows when a
 * click beats the request.
 */
if (!customElements.get('collection-tabs')) {
  class CollectionTabs extends HTMLElement {
    connectedCallback() {
      this.events?.abort();
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      if (!this.tabs.length) return;

      this.viewAllLinks = Array.from(this.querySelectorAll('[data-view-all]'));

      this.tabs.forEach((tab) => {
        tab.addEventListener('click', () => this.select(tab), options);
        tab.addEventListener('keydown', (event) => this.onKeydown(event), options);
      });

      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-catalog-retry]')) this.loadPanels();
      }, options);
      this.syncViewAll(this.selectedTab);
      this.armPrefetch(options);

      if (window.Shopify && window.Shopify.designMode) {
        // Tab ids are `tab-<sectionId>-<blockId>`, so match on the block suffix.
        this.addEventListener('shopify:block:select', (event) => {
          const tab = this.tabs.find((candidate) => candidate.id.endsWith(`-${event.detail.blockId}`));
          if (tab) this.select(tab);
        }, options);
      }
    }

    disconnectedCallback() {
      this.events?.abort();
      this.prefetchObserver?.disconnect();
    }

    /**
     * Fetch hidden panels ahead of the click. Intent signals (pointer/focus on the
     * tab row) fire at once; mere proximity waits for an idle slot so the request
     * never competes with the page's own loading, and is skipped entirely when the
     * visitor asked for reduced data use.
     */
    armPrefetch(options) {
      if (!this.querySelector('[data-deferred-panel]')) return;
      const tablist = this.querySelector('[role="tablist"]');
      if (tablist) {
        const onIntent = () => this.loadPanels();
        tablist.addEventListener('pointerenter', onIntent, options);
        tablist.addEventListener('focusin', onIntent, options);
        tablist.addEventListener('touchstart', onIntent, { ...options, passive: true });
      }

      if (navigator.connection?.saveData) return;
      if (!('IntersectionObserver' in window)) return;
      this.prefetchObserver = new IntersectionObserver((entries, observer) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        const idle = window.requestIdleCallback || ((callback) => setTimeout(callback, 200));
        idle(() => this.loadPanels(), { timeout: 2000 });
      }, { rootMargin: '0px 0px 600px 0px' });
      this.prefetchObserver.observe(this);
    }

    setPanelState(panels, state) {
      panels.forEach((panel) => {
        const status = panel.querySelector('[data-catalog-status]');
        if (status) status.dataset.state = state;
        const message = panel.querySelector('[data-catalog-message]');
        if (message) message.textContent = message.dataset[state] || '';
        panel.toggleAttribute('aria-busy', state === 'loading');
      });
    }

    async loadPanels() {
      if (this.pendingPanels) return this.pendingPanels;
      const panels = [...this.querySelectorAll('[data-deferred-panel]')];
      if (!panels.length) return;
      this.setPanelState(panels, 'loading');
      this.pendingPanels = (async () => {
        try {
          const url = new URL(location.href);
          url.searchParams.delete('sections');
          url.searchParams.set('section_id', this.dataset.sectionId);
          const response = await fetch(url, { signal: AbortSignal.timeout(20000) });
          if (!response.ok) throw new Error('Catalog request failed');
          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          // Validate every source before replacing anything, so a partial/error
          // response leaves all retry controls intact.
          const sources = panels.map((panel) => doc.getElementById(panel.id));
          if (sources.some((source) => !source || source.hasAttribute('data-deferred-panel') || !source.querySelector('swiper-component'))) {
            throw new Error('Catalog response is incomplete');
          }
          if (!this.isConnected) return;
          panels.forEach((panel, index) => {
            const hadFocus = panel.contains(document.activeElement);
            panel.replaceChildren(...sources[index].childNodes);
            panel.removeAttribute('data-deferred-panel');
            panel.removeAttribute('aria-busy');
            panel.style.minHeight = '';
            // Fade the carousel up in place of the skeleton. A hidden panel keeps
            // the class and plays it the first time its tab is opened.
            panel.classList.add('collection-tabs__panel--arrived');
            panel.addEventListener('animationend', function onArrive(event) {
              if (event.animationName !== 'dc-arrive') return;
              panel.classList.remove('collection-tabs__panel--arrived');
              panel.removeEventListener('animationend', onArrive);
            });
            if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(panel);
            if (hadFocus) panel.focus({ preventScroll: true });
          });
          this.updatePanel(this.panelFor(this.selectedTab));
        } catch {
          // The held-open height only makes sense under a skeleton; the notice
          // should not float in a carousel-sized void.
          panels.forEach((panel) => { panel.style.minHeight = ''; });
          this.setPanelState(panels, 'error');
        } finally {
          this.pendingPanels = null;
        }
      })();
      return this.pendingPanels;
    }

    updatePanel(panel) {
      panel?.querySelectorAll('swiper-component').forEach((component) => {
        if (!component.swiper) return;
        component.swiper.update();
        component.updateLock();
      });
    }

    get selectedTab() {
      return this.tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || this.tabs[0];
    }

    panelFor(tab) {
      const id = tab.getAttribute('aria-controls');
      return id ? this.querySelector(`#${CSS.escape(id)}`) : null;
    }

    select(tab, { focusTab = false } = {}) {
      if (!tab) return;

      const previousHeight = this.panelFor(this.selectedTab)?.getBoundingClientRect().height || 0;
      this.tabs.forEach((candidate) => {
        const isTarget = candidate === tab;
        candidate.setAttribute('aria-selected', isTarget ? 'true' : 'false');
        candidate.tabIndex = isTarget ? 0 : -1;

        const panel = this.panelFor(candidate);
        if (panel) panel.toggleAttribute('hidden', !isTarget);
      });

      if (focusTab) tab.focus();

      // Keep the active pill in view inside the scrolling mobile pill row.
      if (typeof tab.scrollIntoView === 'function') {
        tab.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      }

      const panel = this.panelFor(tab);
      if (panel?.hasAttribute('data-deferred-panel')) {
        panel.style.minHeight = `${previousHeight}px`;
        this.loadPanels();
      } else {
        this.updatePanel(panel);
      }

      this.syncViewAll(tab);
    }

    syncViewAll(tab) {
      const url = tab && tab.dataset.collectionUrl;
      if (!url) return;
      this.viewAllLinks.forEach((link) => {
        link.href = url;
      });
    }

    onKeydown(event) {
      const index = this.tabs.indexOf(event.target);
      if (index === -1) return;

      let next = null;

      switch (event.key) {
        case 'ArrowRight':
          next = this.tabs[(index + 1) % this.tabs.length];
          break;
        case 'ArrowLeft':
          next = this.tabs[(index - 1 + this.tabs.length) % this.tabs.length];
          break;
        case 'Home':
          next = this.tabs[0];
          break;
        case 'End':
          next = this.tabs[this.tabs.length - 1];
          break;
        default:
          return;
      }

      event.preventDefault();
      this.select(next, { focusTab: true });
    }
  }

  customElements.define('collection-tabs', CollectionTabs);
}

/**
 * <collection-recommendations> — the panel body of a `collection-tabs` tab.
 *
 * Inert unless it carries `data-url`, which only a tab with `use_recommendations`
 * gets. Liquid's `recommendations` object is populated ONLY when a section is
 * rendered through `routes.product_recommendations_url`, so such a tab ships empty
 * and fills itself: fetch that URL with `section_id`, then lift the carousel out of
 * the matching panel in the response. Scoping by `data-panel-id` (not "the first
 * one in the document") keeps a section with several recommendation tabs correct.
 *
 * The injected `<swiper-component>` initialises itself — its `connectedCallback`
 * runs when the parsed subtree is inserted, and its children are already present.
 *
 * Registration stays guarded for theme-editor reloads. The shared module is
 * fetched and evaluated once even when several sections reference it.
 */
if (!customElements.get('collection-recommendations')) {
  class CollectionRecommendations extends HTMLElement {
    connectedCallback() {
      if (this.hasAttribute('data-pdp-relevance')) {
        this.source = { id: this.dataset.sourceId, type: this.dataset.sourceType, uses: this.array(this.dataset.sourceUses) };
        this.cartIds = (this.dataset.cartIds || '').split(',').filter(Boolean);
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.refreshCart());
        if (!this.dataset.url) this.filterAlternatives();
      }
      if (!this.dataset.url || this.dataset.loaded) return;

      this.observer = new IntersectionObserver(
        (entries, observer) => {
          if (!entries[0].isIntersecting) return;
          observer.unobserve(this);
          this.load();
        },
        { rootMargin: '0px 0px 400px 0px' }
      );
      this.observer.observe(this);
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.unsubscribe?.();
      this.controller?.abort();
      this.cartController?.abort();
    }

    array(value) {
      try { const result = JSON.parse(value || '[]'); return Array.isArray(result) ? result : []; }
      catch { return []; }
    }

    filterAlternatives() {
      if (!this.source) return;
      const track = this.querySelector('.swiper-wrapper');
      if (!track) { this.hideEmptyPanel(); return; }
      this.candidates ||= [...track.querySelectorAll('[data-alternative-id]')].map(node => ({
        id: node.dataset.alternativeId, type: node.dataset.alternativeType,
        uses: this.array(node.dataset.alternativeUses), node,
      }));
      const matches = this.cartStale ? [] : selectAlternatives(this.source, this.candidates, this.cartIds, Number(this.dataset.limit) || 8);
      track.replaceChildren(...matches.map(candidate => candidate.node));
      const section = this.closest('.shopify-section');
      if (section) section.hidden = !matches.length;
      const panel = this.closest('.collection-tabs__panel');
      if (panel) panel.hidden = !matches.length;
      this.querySelector('swiper-component')?.swiper?.update();
    }

    async refreshCart() {
      this.cartController?.abort();
      const controller = new AbortController();
      this.cartController = controller;
      this.controller?.abort();
      this.loadGeneration = (this.loadGeneration || 0) + 1;
      this.cartStale = true;
      this.filterAlternatives();
      const timeout = setTimeout(() => controller.abort(), 3500);
      try {
        const response = await fetch(`${this.dataset.cartUrl}.js`, { signal: controller.signal });
        if (!response.ok) return;
        const cart = await response.json();
        if (controller !== this.cartController || !this.isConnected || !Array.isArray(cart.items)) return;
        this.cartIds = cart.items.flatMap(item => [item.product_id, ...(item.item_components || []).map(component => component.product_id)]).map(String);
        if (this.dataset.relevanceSection && this.dataset.productUrl) {
          const url = new URL(this.dataset.productUrl, location.origin);
          url.searchParams.set('section_id', this.dataset.relevanceSection);
          const sectionResponse = await fetch(url, { signal: controller.signal });
          if (!sectionResponse.ok) return;
          const html = await sectionResponse.text();
          if (controller !== this.cartController || !this.isConnected) return;
          if (this.dataset.url) {
            this.cartStale = false;
            await this.load();
            return;
          }
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const fresh = doc.querySelector('collection-recommendations[data-pdp-relevance]');
          if (!fresh) return;
          this.innerHTML = fresh.innerHTML;
          this.candidates = null;
        }
        this.cartStale = false;
        this.filterAlternatives();
      } catch {} finally { clearTimeout(timeout); }
    }

    async load() {
      this.dataset.loaded = 'true';
      const generation = this.loadGeneration = (this.loadGeneration || 0) + 1;

      const url = `${this.dataset.url}&product_id=${this.dataset.productId}&section_id=${this.dataset.sectionId}`;

      try {
        this.controller?.abort();
        this.controller = new AbortController();
        const response = await fetch(url, { signal: AbortSignal.any([this.controller.signal, AbortSignal.timeout(3500)]) });
        if (!response.ok) throw new Error(`${response.status} ${response.statusText}`);

        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        if (generation !== this.loadGeneration || !this.isConnected) return;
        const source = doc.querySelector(
          `collection-recommendations[data-panel-id="${this.dataset.panelId}"]`
        );

        // No recommendations for this product: an empty carousel would leave a
        // heading over dead space, so retire the panel instead.
        if (!source || !source.querySelector('.swiper-slide')) {
          this.hideEmptyPanel();
          return;
        }

        if (!this.isConnected) return;
        this.innerHTML = source.innerHTML;
        this.candidates = null;
        this.filterAlternatives();
      } catch (error) {
        if (generation !== this.loadGeneration) return;
        this.hideEmptyPanel();
      }
    }

    hideEmptyPanel() {
      const panel = this.closest('.collection-tabs__panel');
      const section = this.closest('.shopify-section');
      if (!panel || !section) return;

      panel.hidden = true;

      // Its tab has nothing left to show either.
      const tab = section.querySelector(`[aria-controls="${panel.id}"]`);
      if (tab) tab.hidden = true;

      // Last panel standing — the whole section is now just a heading.
      const live = Array.from(section.querySelectorAll('.collection-tabs__panel')).filter(
        (candidate) => candidate !== panel
      );
      if (!live.length) section.hidden = true;
    }
  }

  customElements.define('collection-recommendations', CollectionRecommendations);
}
